import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { fetchNotifications } from '@/adapters/fetchExaCare';

interface SuprSendTokenPayload {
  token: string;
  expires_at: number;
}

const QUERY_KEY: string[] = ['useSuprSendJwtTokenQuery'];

const MAX_RETRIES = 6;
const BACKOFF_MULTIPLIER_MS = 2_000;
const MAX_BACKOFF_MS = 30_000;

const SENTRY_TAGS = { feature: 'suprsend-inbox', issue: 'ENG-15345' };

const isNonRetryableStatus = (status?: number) =>
  status === 401 || status === 403;

const reportTerminalFailure = (error: any) => {
  Sentry.captureMessage('SuprSend JWT token fetch failed', {
    level: 'error',
    tags: SENTRY_TAGS,
    extra: { status: error?.response?.status }
  });
};

export const useSuprSendJwtToken = (userId?: string, tenantId?: string) => {
  return useQuery(
    [...QUERY_KEY, userId, tenantId],
    async () => {
      const response = await fetchNotifications.post<SuprSendTokenPayload>(
        `/suprsend/generate-jwt-token`,
        {
          user_id: userId,
          tenant_id: tenantId || 'default',
          expires_in: 3600
        }
      );

      return response;
    },
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: (failureCount, error: any) => {
        if (isNonRetryableStatus(error?.response?.status)) {
          reportTerminalFailure(error);
          return false;
        }
        const shouldRetry = failureCount < MAX_RETRIES;
        if (!shouldRetry) {
          reportTerminalFailure(error);
        }
        return shouldRetry;
      },
      retryDelay: (attemptIndex) =>
        Math.min(BACKOFF_MULTIPLIER_MS * 2 ** attemptIndex, MAX_BACKOFF_MS),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  );
};
